import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/modules/shared/ui/components/collapsible"
import { Button } from "@/modules/shared/ui/components/button"
import { Label } from "@/modules/shared/ui/components/label"
import { Checkbox } from "@/modules/shared/ui/components/checkbox"
import { Input } from "@/modules/shared/ui/components/input"
import { Badge } from "@/modules/shared/ui/components/badge"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/modules/shared/ui/components/select"
import { FaChevronDown, FaChevronRight } from "react-icons/fa"
import { useCurrentOrganization } from "@/modules/organizations/hooks/use-organizations"
import { userService } from "@/modules/users/infrastructure/user-service"
import {
  DocumentPermissionType,
  DOCUMENT_PERMISSION_OPTIONS,
} from "../../dtos/DocumentPermissionType"
import {
  BulkShareRequest,
  BulkShareResult,
} from "../../dtos/BulkPermissionDtos"

interface BulkGrantFormProps {
  onBulkGrant: (request: BulkShareRequest) => void
  isPending: boolean
  lastResult: BulkShareResult | null
}

export function BulkGrantForm({
  onBulkGrant,
  isPending,
  lastResult,
}: BulkGrantFormProps) {
  const org = useCurrentOrganization()
  const orgId = org?.id ?? ""

  const [isOpen, setIsOpen] = useState(false)
  const [emailFilter, setEmailFilter] = useState("")
  const [selectedUserIds, setSelectedUserIds] = useState<Set<string>>(new Set())
  const [permissionType, setPermissionType] =
    useState<DocumentPermissionType>("Read")

  const { data: usersData } = useQuery({
    queryKey: ["org-users", orgId],
    queryFn: () => userService.getOrganizationUsers(orgId),
    enabled: !!orgId && isOpen,
  })

  const filteredUsers = (usersData?.users ?? []).filter(
    (u) =>
      !emailFilter ||
      u.email.toLowerCase().includes(emailFilter.toLowerCase()) ||
      `${u.firstName} ${u.lastName}`
        .toLowerCase()
        .includes(emailFilter.toLowerCase()),
  )

  const toggleUser = (userId: string) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev)
      if (next.has(userId)) next.delete(userId)
      else next.add(userId)
      return next
    })
  }

  const handleSubmit = () => {
    if (selectedUserIds.size === 0) return

    onBulkGrant({
      permissions: Array.from(selectedUserIds).map((userId) => ({
        userId,
        roleId: null,
        permissionType,
      })),
    })

    setSelectedUserIds(new Set())
  }

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button
          type="button"
          className="flex w-full items-center gap-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors py-1"
        >
          {isOpen ? (
            <FaChevronDown className="h-3 w-3" />
          ) : (
            <FaChevronRight className="h-3 w-3" />
          )}
          Bulk Grant
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="space-y-4 pt-3">
        <div className="space-y-2">
          <Label>Filter users</Label>
          <Input
            placeholder="Search by name or email…"
            value={emailFilter}
            onChange={(e) => setEmailFilter(e.target.value)}
          />
        </div>

        <div className="max-h-48 overflow-y-auto rounded-md border divide-y">
          {filteredUsers.length === 0 && (
            <p className="px-3 py-4 text-sm text-muted-foreground text-center">
              No users found
            </p>
          )}
          {filteredUsers.map((u) => (
            <label
              key={u.id}
              className="flex items-center gap-3 px-3 py-2 hover:bg-muted/30 cursor-pointer"
            >
              <Checkbox
                checked={selectedUserIds.has(u.id)}
                onCheckedChange={() => toggleUser(u.id)}
              />
              <span className="text-sm">
                {u.firstName} {u.lastName}{" "}
                <span className="text-muted-foreground">({u.email})</span>
              </span>
            </label>
          ))}
        </div>

        <div className="space-y-2">
          <Label>Permission type for all selected</Label>
          <Select
            value={permissionType}
            onValueChange={(v) =>
              setPermissionType(v as DocumentPermissionType)
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {DOCUMENT_PERMISSION_OPTIONS.map((opt) => (
                <SelectItem key={opt} value={opt}>
                  {opt}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {lastResult && (
          <div className="flex items-center gap-2 text-sm">
            <Badge variant="default" className="bg-green-600">
              {lastResult.granted} granted
            </Badge>
            {lastResult.skipped > 0 && (
              <Badge
                variant="outline"
                className="text-amber-600 border-amber-300"
              >
                {lastResult.skipped} already existed
              </Badge>
            )}
            {lastResult.errors.length > 0 && (
              <Badge variant="destructive">
                {lastResult.errors.length} errors
              </Badge>
            )}
          </div>
        )}

        <Button
          onClick={handleSubmit}
          disabled={isPending || selectedUserIds.size === 0}
          className="w-full"
        >
          {isPending
            ? "Granting…"
            : `Bulk Grant to ${selectedUserIds.size} user${selectedUserIds.size !== 1 ? "s" : ""}`}
        </Button>
      </CollapsibleContent>
    </Collapsible>
  )
}
